/**
 * Decision 0084 (build map §2.3): `AdoptDocument`, the accept of a `propose_page` card. Re-decided as the person who
 * answers: the `proposals/<path>` draft's bytes become the next version of the page at `path` (or its first version).
 */
import {
  docPathKey,
  fromProjectPath,
  isProposalPath,
  proposedPagePathOf,
  workspacePageKindOf,
} from "@minga/protocol"
import { documentByPath } from "../documents"
import { isLive } from "../outputs"
import { artifactOf, memberOf } from "../state"
import type { Artifact, Ids, OptionEffect, ProjectState } from "../types"
import { postArtifact } from "./items"
import { forbidden, invalid, notFound, type Tx } from "./tx"

export const adoptDocument = (
  tx: Tx,
  e: Extract<OptionEffect, { _tag: "AdoptDocument" }>,
  artifactId: Ids["ArtifactId"],
): void => {
  const draft = artifactOf(tx.state, e.artifactId) ?? notFound({ _tag: "artifact", id: e.artifactId })
  const draftPath = draft.doc?.path ?? null
  if (draft.doc === undefined || draftPath === null || !isProposalPath(draftPath) || !isLive(tx.state, draft))
    invalid("only a live proposals/ draft can be adopted", "artifactId")
  if (docPathKey(proposedPagePathOf(draftPath) ?? "") !== docPathKey(e.path))
    invalid(`${draftPath} proposes another page than ${e.path}`, "path")
  const head = documentByPath(tx.state, e.path)
  postArtifact(tx, {
    _tag: "PostArtifact",
    artifactId,
    itemId: null,
    kind: draft.kind,
    title: head?.title ?? draft.title,
    uri: draft.uri,
    contentType: draft.contentType,
    sizeBytes: draft.sizeBytes,
    sha256: draft.sha256,
    preview: null,
    interfaceKey: null,
    supersedes: head?.id ?? null,
    doc: head?.doc !== undefined ? { ...head.doc, path: e.path } : { path: e.path, url: null },
  })
}

type AdoptWorkspace = Extract<OptionEffect, { _tag: "AdoptWorkspaceDocument" }>

/**
 * Decision 0086 (§4.2): why `e` does not name a live `proposals/workspace/<path>` draft for a workspace page at
 * `e.path` with the right `personFor`, or null.
 */
const workspaceDraftProblem = (
  s: ProjectState,
  e: AdoptWorkspace,
  draft: Artifact | undefined,
): string | null => {
  const draftPath = draft?.doc?.path ?? null
  if (!draft || draftPath === null || !isProposalPath(draftPath) || !isLive(s, draft))
    return "AdoptWorkspaceDocument: only a live proposals/workspace/ draft"
  const page = fromProjectPath(e.path)
  const kind = page === null ? null : workspacePageKindOf(page)
  if (kind === null)
    return `${e.path} is not a workspace page (workspace/people/<handle>.md, workspace/guidelines/<topic>.md)`
  if (docPathKey(proposedPagePathOf(draftPath) ?? "") !== docPathKey(e.path))
    return `${draftPath} proposes another page than ${e.path}`
  if (kind === "person" && e.personFor === null) return `${e.path} is a person's page: personFor names them`
  if (kind === "workspace_guideline" && e.personFor !== null)
    return `${e.path} is a guideline: personFor is null`
  return null
}

/**
 * Decision 0086 (§4.2): `AdoptWorkspaceDocument`, re-decided as the person who answers. A person page is accepted
 * only by that person (`personFor`); a workspace guideline by a roster owner or admin. It changes no project state:
 * the option's `SetArtifactState` effects retire the draft, and the project's outbox delivers the adoption to the
 * WorkspaceDO (which re-checks the role and is the truth).
 */
export const adoptWorkspaceDocument = (tx: Tx, e: AdoptWorkspace): void => {
  const draft = artifactOf(tx.state, e.artifactId) ?? notFound({ _tag: "artifact", id: e.artifactId })
  const problem = workspaceDraftProblem(tx.state, e, draft)
  if (problem !== null) invalid(problem, "artifactId")
  const actor = tx.actor
  if (actor._tag !== "user") forbidden("actor_kind", "only a person accepts a workspace page")
  if (e.personFor !== null) {
    if (e.personFor !== actor.userId)
      forbidden("not_owner", `${e.path} is another person's page: only they accept it`)
    return
  }
  const role = memberOf(tx.state, actor.userId)?.role
  if (role !== "owner" && role !== "admin")
    forbidden("not_owner", "only a workspace owner or admin accepts a workspace guideline")
}

/**
 * Decision 0084 (§2.2): the effects of a `propose_page` card an agent's question may offer besides the flow effects:
 * `AdoptDocument` of a live `proposals/` draft for the page it proposes, and `SetArtifactState{rejected}` of any
 * not-yet-rejected version of such a draft (both options retire the whole chain). Null when `e` is one of them and holds; a reason otherwise; undefined when `e` is
 * neither (the ordinary allowlist decides).
 */
export const pageProposalEffectProblem = (s: ProjectState, e: OptionEffect): string | null | undefined => {
  // decision 0086 (§4.2): a workspace page proposal's accept
  if (e._tag === "AdoptWorkspaceDocument") return workspaceDraftProblem(s, e, artifactOf(s, e.artifactId))
  if (e._tag !== "AdoptDocument" && e._tag !== "SetArtifactState") return undefined
  const draft = artifactOf(s, e.artifactId)
  const path = draft?.doc?.path ?? null
  // retiring may name an older, superseded version too: rejecting only the head would make the one before it live
  if (e._tag === "SetArtifactState") {
    if (!draft || path === null || !isProposalPath(path) || draft.state === "rejected")
      return "SetArtifactState on a question: only a proposals/ draft that is not yet rejected"
    return e.state === "rejected" ? null : "a draft can only be retired"
  }
  if (!draft || path === null || !isProposalPath(path) || !isLive(s, draft))
    return `${e._tag} on a question: only a live proposals/ draft`
  return docPathKey(proposedPagePathOf(path) ?? "") === docPathKey(e.path)
    ? null
    : `${path} proposes another page than ${e.path}`
}

/**
 * Decision 0084 (§2.2): an option whose artifact states are all `proposals/` drafts (a propose_page card's accept or
 * decline) is not a review pick, so decision 0050's out-of-date-review guard does not apply to it.
 */
export const onlyRetiresDrafts = (s: ProjectState, effects: ReadonlyArray<OptionEffect>): boolean =>
  effects.every((e) => {
    if (e._tag !== "SetArtifactState") return true
    const path = artifactOf(s, e.artifactId)?.doc?.path ?? null
    return e.state === "rejected" && path !== null && isProposalPath(path)
  })
